/**
 * The language the viewer reads customer mails in.
 *
 * English by default; the browser language when it is one of the six we translate into;
 * the viewer's own choice (remembered in this browser) wins over both. Storage can be
 * missing or blocked, so every read and write is wrapped and simply falls back.
 */

import type { TranslationTarget } from "@/lib/api";

export const VIEWER_LANGUAGE_STORAGE_KEY = "rfq-foundry.viewer-language";

export const DEFAULT_VIEWER_LANGUAGE: TranslationTarget = "en";

/** The choices of the selector, each in its own language. */
export const VIEWER_LANGUAGES: readonly { code: TranslationTarget; label: string }[] = [
  { code: "en", label: "English" },
  { code: "da", label: "Dansk" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "cs", label: "Čeština" },
];

/** The target for a code ("de-AT" reads "de"); null when we do not translate into it. */
export function toViewerLanguage(value: unknown): TranslationTarget | null {
  if (typeof value !== "string") {
    return null;
  }
  const code = value.trim().toLowerCase().split(/[-_]/)[0] ?? "";
  return VIEWER_LANGUAGES.find((entry) => entry.code === code)?.code ?? null;
}

/** The stored choice first, then the first browser language we know, else English. */
export function pickViewerLanguage(
  stored: string | null | undefined,
  browserLanguages: readonly string[],
): TranslationTarget {
  const remembered = toViewerLanguage(stored);
  if (remembered) {
    return remembered;
  }
  for (const language of browserLanguages) {
    const known = toViewerLanguage(language);
    if (known) {
      return known;
    }
  }
  return DEFAULT_VIEWER_LANGUAGE;
}

function browserLanguages(): string[] {
  try {
    if (typeof navigator === "undefined") {
      return [];
    }
    const list = navigator.languages?.length ? [...navigator.languages] : [navigator.language];
    return list.filter((entry): entry is string => typeof entry === "string");
  } catch {
    return [];
  }
}

function storedLanguage(): string | null {
  try {
    return window.localStorage.getItem(VIEWER_LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** The viewer language of this browser. */
export function readViewerLanguage(): TranslationTarget {
  return pickViewerLanguage(storedLanguage(), browserLanguages());
}

/** Remember the choice in this browser. Fails silently without storage. */
export function rememberViewerLanguage(language: TranslationTarget): void {
  try {
    window.localStorage.setItem(VIEWER_LANGUAGE_STORAGE_KEY, language);
  } catch {
    // No storage in this browser: the choice simply lasts for this visit.
  }
}
